import {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {checklistShape} from '../shapes';
import {getLocalDateStr, addDays, endOfWeek, endOfMonth} from '../utils/date';
import TimelineView from './TimelineView';
import Checklist from './Checklist';

const noop = () => {};

function buildItemFilter(timeRange, showOpen, showDone, today, tomorrow, endOfWeekStr, endOfMonthStr) {
    const timeFilters = {
        overdue:   i => i.dueDate < today,
        today:     i => i.dueDate <= today,
        tomorrow:  i => i.dueDate <= tomorrow,
        thisweek:  i => i.dueDate <= endOfWeekStr,
        thismonth: i => i.dueDate <= endOfMonthStr,
        all:       () => true,
    };
    const timeFn = timeFilters[timeRange];
    const statusFn = i => (showOpen && !i.checked) || (showDone && i.checked);
    return i => i.dueDate && timeFn(i) && statusFn(i);
}

export default function ScheduledView({checklistData, onToggleItem, onDeleteItem, onUpdateItem, onStarItem, onSetDueDateItem}) {
    const [timeRange, setTimeRange] = useState('all');
    const [showOpen, setShowOpen] = useState(true);
    const [showDone, setShowDone] = useState(false);
    const [viewMode, setViewMode] = useState('timeline');

    const today = getLocalDateStr(new Date());
    const tomorrow = getLocalDateStr(addDays(new Date(), 1));
    const endOfWeekStr = getLocalDateStr(endOfWeek(new Date()));
    const endOfMonthStr = getLocalDateStr(endOfMonth(new Date()));

    const itemFilter = useMemo(
        () => buildItemFilter(timeRange, showOpen, showDone, today, tomorrow, endOfWeekStr, endOfMonthStr),
        [timeRange, showOpen, showDone, today, tomorrow, endOfWeekStr, endOfMonthStr]
    );

    const timelineItems = useMemo(() => {
        const items = [];
        for (const c of checklistData) {
            for (const i of c.items) {
                if (itemFilter(i)) {
                    items.push({...i, checklistId: c.id, checklistTitle: c.title});
                }
            }
        }
        items.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
        return items;
    }, [checklistData, itemFilter]);

    const filteredChecklists = useMemo(() => {
        if (viewMode !== 'checklist') return [];
        return checklistData
            .filter(c => c.items.some(itemFilter))
            .map(c => ({
                ...c,
                items: c.items.filter(itemFilter).sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
            }));
    }, [checklistData, itemFilter, viewMode]);

    const itemCount = viewMode === 'timeline' ? timelineItems.length : filteredChecklists.reduce((sum, c) => sum + c.items.length, 0);
    const overdueCount = useMemo(() => {
        if (viewMode === 'timeline') return timelineItems.filter(i => !i.checked && i.dueDate < today).length;
        return filteredChecklists.reduce((sum, c) => sum + c.items.filter(i => !i.checked && i.dueDate < today).length, 0);
    }, [viewMode, timelineItems, filteredChecklists, today]);

    const emptyMessages = {
        overdue:   'Nothing overdue. You\'re all caught up!',
        today:     'Nothing due today.',
        tomorrow:  'Nothing due by tomorrow.',
        thisweek:  'Nothing due this week.',
        thismonth: 'Nothing due this month.',
        all:       'No scheduled items. Set a due date on any item to see it here.',
    };
    const emptyMessage = emptyMessages[timeRange];

    const hasItems = itemCount > 0;

    const setTimeRangeOverdue = useCallback(() => setTimeRange('overdue'), []);
    const setTimeRangeToday = useCallback(() => setTimeRange('today'), []);
    const setTimeRangeTomorrow = useCallback(() => setTimeRange('tomorrow'), []);
    const setTimeRangeThisWeek = useCallback(() => setTimeRange('thisweek'), []);
    const setTimeRangeThisMonth = useCallback(() => setTimeRange('thismonth'), []);
    const setTimeRangeAll = useCallback(() => setTimeRange('all'), []);
    const toggleOpen = useCallback(() => setShowOpen(v => { if (v && !showDone) return v; return !v; }), [showDone]);
    const toggleDone = useCallback(() => setShowDone(v => { if (v && !showOpen) return v; return !v; }), [showOpen]);
    const setViewTimeline = useCallback(() => setViewMode('timeline'), []);
    const setViewChecklist = useCallback(() => setViewMode('checklist'), []);

    return (
        <div className="scheduled-view">
            <div className="scheduled-filter-bar">
                <div className="scheduled-filter-group" role="group" aria-label="Time range">
                    <span className="scheduled-filter-label">When</span>
                    <div className="timeline-segmented-control">
                        <button className={`timeline-segment${timeRange === 'overdue' ? ' timeline-segment--active' : ''}`} onClick={setTimeRangeOverdue}>Overdue</button>
                        <button className={`timeline-segment${timeRange === 'today' ? ' timeline-segment--active' : ''}`} onClick={setTimeRangeToday}>Today</button>
                        <button className={`timeline-segment${timeRange === 'tomorrow' ? ' timeline-segment--active' : ''}`} onClick={setTimeRangeTomorrow}>Tomorrow</button>
                        <button className={`timeline-segment${timeRange === 'thisweek' ? ' timeline-segment--active' : ''}`} onClick={setTimeRangeThisWeek}>This Week</button>
                        <button className={`timeline-segment${timeRange === 'thismonth' ? ' timeline-segment--active' : ''}`} onClick={setTimeRangeThisMonth}>This Month</button>
                        <button className={`timeline-segment${timeRange === 'all' ? ' timeline-segment--active' : ''}`} onClick={setTimeRangeAll}>All</button>
                    </div>
                </div>
                <div className="scheduled-filter-group" role="group" aria-label="Status filter">
                    <span className="scheduled-filter-label">Status</span>
                    <div className="timeline-segmented-control">
                        <button className={`timeline-segment${showOpen ? ' timeline-segment--active' : ''}`} onClick={toggleOpen}>Open</button>
                        <button className={`timeline-segment${showDone ? ' timeline-segment--active' : ''}`} onClick={toggleDone}>Done</button>
                    </div>
                </div>
                <div className="scheduled-filter-group" role="group" aria-label="View mode">
                    <span className="scheduled-filter-label">View</span>
                    <div className="timeline-segmented-control">
                        <button className={`timeline-segment${viewMode === 'timeline' ? ' timeline-segment--active' : ''}`} onClick={setViewTimeline}>Timeline</button>
                        <button className={`timeline-segment${viewMode === 'checklist' ? ' timeline-segment--active' : ''}`} onClick={setViewChecklist}>By Checklist</button>
                    </div>
                </div>
                {hasItems && (
                    <div className="scheduled-summary">
                        <span>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                        {overdueCount > 0 && (
                            <span className="scheduled-summary-overdue">{overdueCount} overdue</span>
                        )}
                    </div>
                )}
            </div>

            {!hasItems ? (
                <div className="empty-state">
                    <p className="empty-state-text">{emptyMessage}</p>
                </div>
            ) : viewMode === 'timeline' ? (
                <TimelineView
                    items={timelineItems}
                    onToggleItem={onToggleItem}
                    onDeleteItem={onDeleteItem}
                    onUpdateItem={onUpdateItem}
                    onStarItem={onStarItem}
                    onSetDueDateItem={onSetDueDateItem}
                />
            ) : (
                <div className="checklists-container">
                    {filteredChecklists.map(checklist => (
                        <div key={checklist.id} className="sortable-checklist-wrapper" id={`checklist-${checklist.id}`}>
                            <Checklist
                                data={checklist}
                                readOnly
                                onDelete={noop}
                                onAddItem={noop}
                                onToggleItem={onToggleItem}
                                onDeleteItem={onDeleteItem}
                                onUpdateTitle={noop}
                                onUpdateItem={onUpdateItem}
                                onReorderItems={noop}
                                onToggleSortMode={noop}
                                onToggleSinkChecked={noop}
                                onToggleHideChecked={noop}
                                onStarItem={onStarItem}
                                onSetDueDateItem={onSetDueDateItem}
                                isFocused={false}
                                onFocus={noop}
                                onUnfocus={noop}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

ScheduledView.propTypes = {
    checklistData: PropTypes.arrayOf(checklistShape).isRequired,
    onToggleItem: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
    onStarItem: PropTypes.func.isRequired,
    onSetDueDateItem: PropTypes.func.isRequired,
};
